import React, { type FC, memo, use, useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Button, Label, Modal, Switch, Tooltip } from "@heroui/react";
import { Sparkles } from "@gravity-ui/icons";

import { AgentRunYearContext } from "./agentLinks";
import { readAgentMode, writeAgentMode } from "./agentModeStorage";
import { AgentMessageView } from "./AgentMessageView";
import { AgentRunPill } from "./AgentRunPill";
import { AgentStepProgress } from "./AgentStepList";
import { AGENT_STEPS } from "./steps";
import type { AgentContext, AgentMode, AgentStep } from "./runner/types";
import { useAgentContext } from "./useAgentContext";
import { type AgentRun, useAgentRun } from "./useAgentRun";
import { CompanyContext } from "@domain/context/CompanyContext";
import { useTranslation } from "@domain/intl/useTranslation";

interface ModalProps {
    isOpen: boolean;
    onClose(): void;
    /** Defaults to the full scenario; tests pass their own. */
    steps?: AgentStep[];
    /** Defaults to `useAgentContext()`; tests pass a fake. */
    context?: AgentContext;
}

interface ChoiceProps {
    value: string;
    primary?: boolean;
    onAnswer(value: string): void;
    children: React.ReactNode;
}

const ChoiceButton: FC<ChoiceProps> = ({ value, primary, onAnswer, children }) => {
    const ref = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        if (primary) ref.current?.focus();
    }, [primary]);
    const handlePress = useCallback(() => onAnswer(value), [onAnswer, value]);
    return (
        <Button ref={ref} variant={primary ? "primary" : "secondary"} className="rounded-full" onPress={handlePress}>
            {children}
        </Button>
    );
};

interface ChatProps {
    run: AgentRun;
    context: AgentContext;
    onClose(): void;
    /** Hides the modal while the run goes on; without it the modal offers no Minimieren. */
    onMinimize?(): void;
}

const AgentChat: FC<ChatProps> = ({ run, context, onClose, onMinimize }) => {
    const { t } = useTranslation();
    const { companyGQL } = use(CompanyContext);
    const { state, cancel, setMode, busy } = run;
    const [confirmStop, setConfirmStop] = useState(false);
    const logRef = useRef<HTMLDivElement>(null);

    const handleModeSwitch = useCallback(
        (quick: boolean) => {
            const next: AgentMode = quick ? "quick" : "approval";
            writeAgentMode(context.companyId, next);
            setMode(next);
        },
        [context.companyId, setMode],
    );
    useEffect(() => {
        const log = logRef.current;
        if (log) log.scrollTop = log.scrollHeight;
    }, [run.messages]);

    // While the finAPI form is open the run waits for the bank, so pausing makes no sense; the user may skip the bank
    const bankImportMessageId = run.messages.find((m) => m.panel?.type === "bankImport")?.id;
    const { completePanel } = run;
    const handleSkipBank = useCallback(() => {
        if (bankImportMessageId) completePanel(bankImportMessageId, "skipped");
    }, [completePanel, bankImportMessageId]);

    const isActive = state === "running" || state === "pausing" || state === "paused";
    const handleClose = useCallback(() => {
        if (isActive) {
            setConfirmStop(true);
        } else {
            onClose();
        }
    }, [isActive, onClose]);
    const handleConfirmStop = useCallback(() => {
        // An answer that landed meanwhile started a server call; ending now would cut it off.
        if (busy) {
            setConfirmStop(false);
            return;
        }
        cancel();
        onClose();
    }, [busy, cancel, onClose]);
    const handleKeep = useCallback(() => setConfirmStop(false), []);
    const handleOpenChange = useCallback((open: boolean) => !open && handleClose(), [handleClose]);

    const awaitingApproval =
        run.pending !== null && run.messages.some((m) => m.kind === "approval" && !m.approval?.answer);
    let pendingKey = "";
    for (const m of run.messages) if (m.kind === "question") pendingKey = m.id;
    const handleAnswer = run.pending ? run.answer : undefined;
    // One mode control at a time: the footer switch waits until the mode card is answered.
    const modeChoiceOpen = run.messages.some((m) => m.panel?.type === "modeChoice");

    const modeSwitch = modeChoiceOpen ? null : (
        <Switch size="sm" isSelected={run.mode === "quick"} onChange={handleModeSwitch}>
            <Switch.Content>
                <Switch.Control>
                    <Switch.Thumb />
                </Switch.Control>
                <Label>{t("app.agent.mode.quickSwitch")}</Label>
            </Switch.Content>
        </Switch>
    );

    const subtitle = [companyGQL?.name, String(context.year)].join(" · ");

    return (
        <Modal.Backdrop isOpen onOpenChange={handleOpenChange} isDismissable={!busy} isKeyboardDismissDisabled={busy}>
            <Modal.Container>
                <Modal.Dialog
                    className="w-140 max-w-full overflow-hidden p-0"
                    data-testid="binale-agent-modal"
                    aria-busy={busy}
                >
                    {busy ? (
                        <Tooltip delay={0} closeDelay={0}>
                            <Tooltip.Trigger data-testid="agent-close-locked">
                                <Modal.CloseTrigger isDisabled />
                            </Tooltip.Trigger>
                            <Tooltip.Content placement="bottom end">{t("app.agent.closeLocked")}</Tooltip.Content>
                        </Tooltip>
                    ) : (
                        <Modal.CloseTrigger onPress={handleClose} />
                    )}
                    <AgentStepProgress
                        steps={run.stepIds}
                        stepStates={run.stepStates}
                        stepNumber={run.stepNumber}
                        finished={state === "finished"}
                    >
                        <Modal.Header className="flex flex-row items-center gap-2 px-4 py-3 text-left">
                            <Sparkles aria-hidden className="size-4" />
                            <Modal.Heading className="text-white">{t("app.agent.title")}</Modal.Heading>
                            <small data-testid="agent-header-info" className="ml-auto mr-2 opacity-90">
                                {subtitle}
                            </small>
                            <span
                                data-testid="agent-header-mode"
                                className={`${onMinimize ? "" : "mr-8 "}rounded-full bg-white/20 px-2 py-0.5 text-xs text-white`}
                            >
                                {t(run.mode === "quick" ? "app.agent.mode.quick" : "app.agent.mode.approval")}
                            </span>
                            {onMinimize ? (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="mr-8 text-white"
                                    isDisabled={run.panelBusy}
                                    onPress={onMinimize}
                                >
                                    {t("app.agent.minimize")}
                                </Button>
                            ) : null}
                        </Modal.Header>
                    </AgentStepProgress>
                    <Modal.Body
                        role="log"
                        inert={confirmStop}
                        aria-live="polite"
                        className="flex h-96 flex-col gap-2 overflow-y-auto bg-default/40 p-3"
                        ref={logRef}
                    >
                        <AgentRunYearContext value={context.year}>
                            <AnimatePresence initial={false}>
                                {run.messages.map((message) => (
                                    <AgentMessageView
                                        key={message.id}
                                        message={message}
                                        onPanelDone={run.completePanel}
                                        onPanelBusy={run.setPanelBusy}
                                        onRerun={state === "finished" ? run.rerunStep : undefined}
                                        onAnswer={handleAnswer}
                                    />
                                ))}
                            </AnimatePresence>
                        </AgentRunYearContext>
                    </Modal.Body>
                    <Modal.Footer className="flex flex-wrap justify-start gap-2 border-t border-separator p-3">
                        {confirmStop ? (
                            <div
                                role="alertdialog"
                                aria-label={t("app.agent.confirmStop.title")}
                                className="flex w-full flex-col gap-2"
                            >
                                <strong>{t("app.agent.confirmStop.title")}</strong>
                                <span className="text-sm text-muted">{t("app.agent.confirmStop.body")}</span>
                                <div className="flex gap-2">
                                    <Button variant="danger" onPress={handleConfirmStop}>
                                        {t("app.agent.stop")}
                                    </Button>
                                    <Button variant="outline" onPress={handleKeep}>
                                        {t("app.agent.confirmStop.keep")}
                                    </Button>
                                </div>
                            </div>
                        ) : awaitingApproval ? (
                            <>
                                <span className="text-sm text-muted">{t("app.agent.approve.waiting")}</span>
                                {modeSwitch}
                                <Button variant="outline" className="ml-auto" onPress={handleClose}>
                                    {t("app.agent.stop")}
                                </Button>
                            </>
                        ) : run.pending ? (
                            run.pending.choices.map((choice) => (
                                <ChoiceButton
                                    key={`${pendingKey}-${choice.value}`}
                                    value={choice.value}
                                    primary={choice.primary}
                                    onAnswer={run.answer}
                                >
                                    {t(choice.labelId)}
                                </ChoiceButton>
                            ))
                        ) : isActive ? (
                            <>
                                {modeSwitch}
                                {bankImportMessageId ? (
                                    <Button variant="secondary" isDisabled={run.panelBusy} onPress={handleSkipBank}>
                                        {t("app.agent.bankImport.skipBank")}
                                    </Button>
                                ) : state === "paused" || state === "pausing" ? (
                                    <Button variant="primary" onPress={run.resume}>
                                        {t("app.agent.resume")}
                                    </Button>
                                ) : (
                                    <Button variant="secondary" onPress={run.pause}>
                                        {t("app.agent.pause")}
                                    </Button>
                                )}
                                <Button variant="outline" isDisabled={busy} onPress={handleClose}>
                                    {t("app.agent.stop")}
                                </Button>
                            </>
                        ) : (
                            <Button variant="primary" onPress={onClose}>
                                {t("app.agent.close")}
                            </Button>
                        )}
                    </Modal.Footer>
                </Modal.Dialog>
            </Modal.Container>
        </Modal.Backdrop>
    );
};

interface SessionProps {
    isOpen: boolean;
    steps: AgentStep[];
    /** Defaults to `useAgentContext()`; tests pass a fake. */
    context?: AgentContext;
    onClose(): void;
    onMinimize?(): void;
    /** Reopens the modal from the pill. */
    onRestore?(): void;
    /** Reports every new run state up to the provider. */
    onRun?(run: AgentRun): void;
}

type SessionViewProps = Omit<SessionProps, "context"> & { context: AgentContext };

const AgentRunSessionView: FC<SessionViewProps> = ({
    isOpen,
    steps,
    context,
    onClose,
    onMinimize,
    onRestore,
    onRun,
}) => {
    const [initialMode] = useState(() => readAgentMode(context.companyId));
    const run = useAgentRun(steps, context, initialMode);
    const { start, busy } = run;

    useEffect(() => start(), [start]);
    useEffect(() => onRun?.(run), [onRun, run]);
    useEffect(() => {
        if (!busy) {
            return undefined;
        }
        const ask = (e: BeforeUnloadEvent) => e.preventDefault();
        window.addEventListener("beforeunload", ask);
        return () => window.removeEventListener("beforeunload", ask);
    }, [busy]);

    if (isOpen) {
        return <AgentChat run={run} context={context} onClose={onClose} onMinimize={onMinimize} />;
    }
    return onRestore ? <AgentRunPill run={run} onRestore={onRestore} /> : null;
};

const AgentRunSessionWithContext: FC<Omit<SessionProps, "context">> = (props) => {
    const context = useAgentContext();
    return <AgentRunSessionView {...props} context={context} />;
};

/** One run from start to close: owns the run state and shows it as the modal or, minimized, as the pill. */
const AgentRunSessionImpl: FC<SessionProps> = ({ context, ...props }) =>
    context ? <AgentRunSessionView {...props} context={context} /> : <AgentRunSessionWithContext {...props} />;

export const AgentRunSession = memo(AgentRunSessionImpl);

const BinaleAgentModalImpl: FC<ModalProps> = ({ isOpen, onClose, steps = AGENT_STEPS, context }) => {
    // Content mounts per open, so each opening is a fresh run and a fresh context.
    if (!isOpen) return null;
    return <AgentRunSession isOpen steps={steps} context={context} onClose={onClose} />;
};

/** A single run without the app-level provider; the app uses `AgentRunProvider`. */
export const BinaleAgentModal = memo(BinaleAgentModalImpl);
